import React, {useMemo} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faBinoculars,
    faCalendarDays,
    faChevronLeft,
    faCircleCheck,
    faClock,
    faCrow,
    faLocationDot,
    faSkullCrossbones
} from "@fortawesome/free-solid-svg-icons";
import {EBirdChecklistByFamily, EBirdChecklistBySpecies, EBirdObservationsBySpecies} from "ebird-mydata-reader";
import {useLocation, useNavigate} from "react-router-dom";
import {getFamilySlug} from "../../helpers/helpers";
import {getRandomDashboardHeroImage} from "../../helpers/dashboardHeroImages";
import {toRootRelativeImagePath} from "../../helpers/imagePaths";
import {ChecklistFamilyList} from "./checklist-family-list";
import {ChecklistFamilyDetail} from "./checklist-family-detail";
import {ChecklistSpeciesDetail, getSpeciesObservations, getSpeciesStats} from "./checklist-species-detail";
import {
    getFamilyCommonName,
    getFamilyScientificName,
    getFamilyStats,
    getShortDate,
    getSpeciesStatusLabel
} from "./checklist-helpers";
import './checklist.scss';

export type ChecklistProps = {
    checklist: EBirdChecklistByFamily[],
    observationsBySpecies: EBirdObservationsBySpecies[]
}

export default function Checklist({checklist, observationsBySpecies}: ChecklistProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const familySlug = location.pathname.startsWith('/checklist/family/')
        ? decodeURIComponent(location.pathname.replace('/checklist/family/', '').split('/')[0])
        : undefined;
    const speciesCode = location.pathname.includes('/species/')
        ? decodeURIComponent(location.pathname.split('/species/')[1]?.split('/')[0] ?? '')
        : undefined;
    const selectedFamily = useMemo(() => {
        if (!familySlug) return null;
        return checklist.find(family => getFamilySlug(family.familyName) === familySlug) ?? null;
    }, [checklist, familySlug]);
    const selectedSpecies = useMemo(() => {
        if (!selectedFamily || !speciesCode) return null;

        return selectedFamily.speciesList.find(species => {
            return species.speciesCode === speciesCode || species.alternateSpeciesCodes?.includes(speciesCode);
        }) ?? null;
    }, [selectedFamily, speciesCode]);
    const checklistHeroImage = useMemo(() => {
        return getRandomDashboardHeroImage();
    }, []);
    const selectedFamilyHeroImage = useMemo(() => {
        if (!selectedFamily) return undefined;

        const familyImage = selectedSpecies?.image ?? selectedFamily.speciesList[0]?.image;
        return toRootRelativeImagePath(familyImage?.medium ?? familyImage?.thumb);
    }, [selectedFamily, selectedSpecies]);
    const heroImage = selectedFamilyHeroImage ?? checklistHeroImage;
    const selectedFamilyStats = useMemo(() => {
        return selectedFamily ? getFamilyStats(selectedFamily) : null;
    }, [selectedFamily]);
    const selectedSpeciesStats = useMemo(() => {
        if (!selectedSpecies) return null;

        return getSpeciesStats(
            selectedSpecies,
            getSpeciesObservations(selectedSpecies, observationsBySpecies)
        );
    }, [observationsBySpecies, selectedSpecies]);
    const checklistStats = useMemo(() => {
        const familyStats = checklist.map(getFamilyStats);
        const seenSpecies = familyStats.reduce((total, stats) => total + stats.seenSpecies, 0);
        const totalSpecies = familyStats.reduce((total, stats) => total + stats.totalSpecies, 0);
        const years = new Set<number>();

        for (const family of checklist) {
            for (const species of family.speciesList) {
                if (!species.seen || !species.firstObservation?.date) continue;

                const year = parseInt(species.firstObservation.date.split('-')[0]);
                if (!Number.isNaN(year)) {
                    years.add(year);
                }
            }
        }

        return {
            seenSpecies,
            totalSpecies,
            unseenSpecies: Math.max(totalSpecies - seenSpecies, 0),
            seenFamilies: checklist.filter(family => {
                const stats = getFamilyStats(family);
                return !stats.isExtinct && family.seen;
            }).length,
            unseenFamilies: checklist.filter(family => {
                const stats = getFamilyStats(family);
                return !stats.isExtinct && !family.seen;
            }).length,
            extinctFamilies: familyStats.filter(stats => stats.isExtinct).length,
            years: years.size
        };
    }, [checklist]);

    const handleChangeFamily = (family: EBirdChecklistByFamily) => {
        navigate(`/checklist/family/${getFamilySlug(family.familyName)}`);
    }

    const handleShowAllFamilies = () => {
        navigate('/checklist');
    }

    const handleShowFamily = () => {
        if (!selectedFamily) return;

        navigate(`/checklist/family/${getFamilySlug(selectedFamily.familyName)}`);
    }

    const handleChangeSpecies = (species: EBirdChecklistBySpecies) => {
        if (!selectedFamily) return;

        navigate(`/checklist/family/${getFamilySlug(selectedFamily.familyName)}/species/${species.speciesCode}`);
    }

    const renderSpeciesStatus = (species: EBirdChecklistBySpecies) => (
        <span className={`checklist__species-status ${species.isExtinct ? 'checklist__species-status--extinct' : species.seen ? 'checklist__species-status--seen' : ''}`}>
            <FontAwesomeIcon icon={species.isExtinct ? faSkullCrossbones : species.seen ? faCircleCheck : faCrow} aria-hidden="true" />
            {getSpeciesStatusLabel(species)}
        </span>
    );

    const renderMetric = (icon: any, label: string, value: string|number, helper?: string) => (
        <div className="checklist__species-metric">
            <span className="checklist__species-metric-icon" aria-hidden="true">
                <FontAwesomeIcon icon={icon} />
            </span>
            <span>
                <span>{label}</span>
                <strong>{value}</strong>
                {helper ? <small>{helper}</small> : null}
            </span>
        </div>
    );

    const renderHeroBack = () => {
        if (selectedSpecies && selectedFamily) {
            return (
                <button type="button" className="checklist__hero-back" onClick={handleShowFamily}>
                    <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
                    {getFamilyScientificName(selectedFamily)} ({getFamilyCommonName(selectedFamily)})
                </button>
            );
        }

        if (selectedFamily) {
            return (
                <button type="button" className="checklist__hero-back" onClick={handleShowAllFamilies}>
                    <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
                    All Families
                </button>
            );
        }

        return null;
    }

    const getHeroEyebrow = () => {
        if (selectedSpecies) return 'Species Detail';
        if (selectedFamily) return 'Family Detail';
        return 'Family Explorer';
    }

    const renderHeroStats = () => {
        if (selectedSpecies) {
            if (!selectedSpecies.seen || !selectedSpeciesStats) return null;

            return (
                <div className="checklist__species-metrics" aria-label="Species summary">
                    {renderMetric(faCalendarDays, 'First Seen', getShortDate(selectedSpeciesStats.firstObservation?.date) || '--')}
                    {renderMetric(faClock, 'Most Recent', getShortDate(selectedSpeciesStats.latestObservation?.date) || '--')}
                    {renderMetric(faBinoculars, 'Total Observations', selectedSpeciesStats.observationCount.toLocaleString())}
                    {renderMetric(faLocationDot, 'Unique Locations', selectedSpeciesStats.locationCount.toLocaleString())}
                </div>
            );
        }

        return (
            <div className="checklist__stats" aria-label="Checklist summary">
                <div className="checklist__stat">
                    <span className="checklist__stat-icon" aria-hidden="true">
                        <FontAwesomeIcon icon={faBinoculars} />
                    </span>
                    <span>
                        <strong>{(selectedFamilyStats?.seenSpecies ?? checklistStats.seenSpecies).toLocaleString()}</strong>
                        <span>Species Seen</span>
                    </span>
                </div>
                {selectedFamily ?
                    <>
                        <div className="checklist__stat">
                            <span className="checklist__stat-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faCrow} />
                            </span>
                            <span>
                                <strong>{(selectedFamilyStats?.seenPercent ?? 0).toLocaleString()}%</strong>
                                <span>Species Seen</span>
                            </span>
                        </div>
                    </> :
                    <>
                        <div className="checklist__stat">
                            <span className="checklist__stat-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faCrow} />
                            </span>
                            <span>
                                <strong>{checklistStats.totalSpecies.toLocaleString()}</strong>
                                <span>Total Species</span>
                            </span>
                        </div>
                        <div className="checklist__stat">
                            <span className="checklist__stat-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faCalendarDays} />
                            </span>
                            <span>
                                <strong>{checklistStats.years.toLocaleString()}</strong>
                                <span>{checklistStats.years === 1 ? 'Year Birding' : 'Years Birding'}</span>
                            </span>
                        </div>
                    </>
                }
            </div>
        );
    }

    const renderChecklistView = () => {
        if (selectedFamily && selectedSpecies) {
            return (
                <ChecklistSpeciesDetail
                    family={selectedFamily}
                    species={selectedSpecies}
                    observationsBySpecies={observationsBySpecies}
                />
            );
        }

        if (selectedFamily) {
            return (
                <ChecklistFamilyDetail
                    family={selectedFamily}
                    observationsBySpecies={observationsBySpecies}
                    onChangeSpecies={handleChangeSpecies}
                />
            );
        }

        return (
            <ChecklistFamilyList
                checklist={checklist}
                checklistStats={checklistStats}
                onChangeFamily={handleChangeFamily}
            />
        );
    }

    return (
        <section
            className="checklist"
            aria-labelledby="checklist-title"
            style={{'--checklist-hero-image': `url("${heroImage}")`} as React.CSSProperties}
        >
            <div className={`checklist__hero ${selectedFamily ? 'checklist__hero--family' : ''} ${selectedSpecies ? 'checklist__hero--species' : ''}`}>
                <div className="checklist__hero-content">
                    {renderHeroBack()}
                    <p className="checklist__eyebrow">{getHeroEyebrow()}</p>
                    <h1 id="checklist-title" className="checklist__title">
                        {selectedSpecies ? selectedSpecies.commonName : selectedFamily ? getFamilyScientificName(selectedFamily) : 'See the Bigger Picture'}
                    </h1>
                    <p className="checklist__intro">
                        {selectedSpecies ?
                            selectedSpecies.scientificName :
                            selectedFamily ?
                            getFamilyCommonName(selectedFamily) :
                            'Explore bird families and check what you’ve seen so far.'
                        }
                    </p>
                    {selectedSpecies ? renderSpeciesStatus(selectedSpecies) : null}
                    {renderHeroStats()}
                </div>
            </div>

            <div className="checklist__panel">
                {renderChecklistView()}
            </div>
        </section>
    );
}
